import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { StoryBoardTracks } from 'services/storyboard.service';

export type LinkedIndex = {
  index: Index;
  isLastInGroup: boolean;
  isFirstInGroup: boolean;
};

export type SbPlaybackParams = {
  startTime: Seconds;
  endTime: Seconds;
  idleStart: Seconds;
  idleEnd: Seconds;
  cellIndex: Index;
  rate: number;
  linkedIndex: LinkedIndex | null;
};
export type SbClipPlaybackMap = Record<ClipId, SbPlaybackParams>;
export const SB_CLIP_PLAYBACK_PARAMS: SbPlaybackParams = {
  startTime: 0,
  endTime: 0,
  cellIndex: 0,
  idleStart: 0,
  idleEnd: 0,
  rate: 1,
  linkedIndex: null,
};
export type ErrorRateValue = 'max' | 'min';
export type ErrorRateMap = Record<ClipId, ErrorRateValue>;
export type SetErrorRate = { clipId: ClipId; value?: ErrorRateValue };

export type SbTrackState = 'collapsed' | null;
export type SbTracksMode = Record<StoryBoardTracks, SbTrackState>;
export type SbViewMode = {
  audio: SbTrackState;
  sbTracks: SbTracksMode;
  prevSbTracks: SbTracksMode | null;
};

interface State {
  sbWsPlayback: boolean;
  sbPlayback: boolean;
  isIdle: boolean;
  selectedClips: CellClip[];
  transitionEndListener: { player: Index } | null;
  readyPlayersListener: Record<string, never>;
  sbLoaderListener: Record<string, never>;
  sbClipPlaybackMap: SbClipPlaybackMap;
  errorRateMap: ErrorRateMap;
  cellResizeHighlight: boolean;
  playerSeekEnabled: boolean;
  panZoomControl: boolean;
  scrollSyncLock: boolean;
  sbViewMode: SbViewMode;
}

const initialState: State = {
  sbWsPlayback: false,
  sbPlayback: false,
  isIdle: false,
  selectedClips: [],
  readyPlayersListener: {},
  transitionEndListener: null,
  cellResizeHighlight: false,
  playerSeekEnabled: false,
  panZoomControl: false,
  sbClipPlaybackMap: {},
  scrollSyncLock: true,
  sbLoaderListener: {},
  errorRateMap: {},
  sbViewMode: {
    sbTracks: {
      storyBoard: null,
      clipBoard: null,
      discardBoard: null,
    },
    audio: 'collapsed',
    prevSbTracks: null,
  },
};

// S L I C E

export const storyboardSlice = createSlice({
  name: 'sb',
  initialState,
  reducers: {
    setSbWsPlayback: (state, action: PayloadAction<boolean | undefined>) => {
      state.sbWsPlayback = action.payload ?? !state.sbWsPlayback;
    },
    setSbPlayback: (state, action: PayloadAction<boolean | undefined>) => {
      state.sbPlayback = action.payload ?? !state.sbPlayback;
    },
    setIsIdle: (state, action: PayloadAction<boolean>) => {
      state.isIdle = action.payload;
    },
    setSelectedClips: (state, action: PayloadAction<ClipId[]>) => {
      state.selectedClips = action.payload;
    },
    setTransitionEndListener: (state, action: PayloadAction<Index>) => {
      state.transitionEndListener = { player: action.payload };
    },
    setReadyPlayersListener: (state) => {
      state.readyPlayersListener = {};
    },
    setSbLoaderListener: (state) => {
      state.sbLoaderListener = {};
    },
    setCellResizeHighlight: (state, action: PayloadAction<boolean>) => {
      state.cellResizeHighlight = action.payload;
    },
    setPlayerSeekEnabled: (state, action: PayloadAction<boolean>) => {
      state.playerSeekEnabled = action.payload;
    },
    setPanZoomControl: (state, action: PayloadAction<boolean>) => {
      state.panZoomControl = action.payload;
    },
    setScrollSyncLock: (state) => {
      state.scrollSyncLock = !state.scrollSyncLock;
    },
    setSbClipPlaybackMap: (state, action: PayloadAction<SbClipPlaybackMap>) => {
      Object.entries(action.payload).forEach(([clipId, data]) => {
        state.sbClipPlaybackMap[clipId] = data;
      });
    },
    setErrorRate: (state, action: PayloadAction<SetErrorRate>) => {
      const { clipId, value } = action.payload;
      if (value) state.errorRateMap[clipId] = value;
      else delete state.errorRateMap[clipId];
    },
    setView: (state, action: PayloadAction<Partial<SbViewMode>>) => {
      state.sbViewMode = { ...state.sbViewMode, ...action.payload };
    },
    resetStoryboard: (state) => {
      return { ...initialState, sbViewMode: state.sbViewMode };
    },
  },
});

export const {
  setSbPlayback,
  setSbWsPlayback,
  setSelectedClips,
  setPanZoomControl,
  setSbLoaderListener,
  setCellResizeHighlight,
  setReadyPlayersListener,
  setTransitionEndListener,
  setPlayerSeekEnabled,
  setSbClipPlaybackMap,
  setScrollSyncLock,
  resetStoryboard,
  setErrorRate,
  setIsIdle,
  setView,
} = storyboardSlice.actions;
export { StoryBoardTracks };
export default storyboardSlice.reducer;
